import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { DateTimePicker, DropdownList } from 'react-widgets';
import moment from 'moment';
import WorkedHoursReportGrid from './components/WorkedHoursReportGrid';
import { Paginated } from 'src/services/api-handlers/pagination';
import RoleLayout from 'src/components/layout/RoleLayout';
import Loader from '../../../../../components/Loader';
import { IWorkedHoursReport } from 'src/store/reducers/workedHoursReport-reducer';
import GeneralEnumService from '../../../../../enumerables/general-enum';
import { getValFromEnum } from '../../../../../components/redux-form/utils/dropdown-values';
import gridStyles from 'src/components/react-table/assets/grid.module.scss';
import styles from './assets/screen.module.scss';
import OrderEnum from '../../../../../enumerables/order-enum';

export interface StateProps {
    data: Paginated<IWorkedHoursReport>;
    inProgress: boolean;
}

export interface DispatchProps {
    getData: (params: any) => void;
    getDataCsv: (params: any) => void;
}

export interface OwnProps {}

export interface Props extends StateProps, DispatchProps, OwnProps {}

const WorkedHoursReportList: React.FC<Props> = ({ data, inProgress, getData, getDataCsv }) => {
    const comparisonEnum = GeneralEnumService ? GeneralEnumService.getEnum('ComparisonCondition') : {};
    const comparisonOptions: SelectObject[] = getValFromEnum(comparisonEnum);

    const serviceTypeProviderEnum = OrderEnum ? OrderEnum.getEnum('ServiceTypeProvider') : {};
    const serviceTypeProviderOptions: SelectObject[] = [
        { value: '', label: 'All' },
        ...getValFromEnum(serviceTypeProviderEnum),
    ];

    const periodEnum = GeneralEnumService ? GeneralEnumService.getEnum('PeriodCondition') : {};
    const periodOptions: SelectObject[] = getValFromEnum(periodEnum);

    const [comparison, setComparison] = useState<SelectObject | null>(null);
    const [period, setPeriod] = useState<SelectObject | null>(null);
    const [serviceTypeProvider, setServiceTypeProvider] = useState<SelectObject | null>(null);
    const [hours, setHours] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [hasSearched, setHasSearched] = useState(false);

    const isValid =
        comparison !== null && period !== null && hours !== '' && dateFrom !== undefined && dateTo !== undefined;

    const buildParams = () => ({
        comparison: comparison?.value,
        period: period?.value,
        hours: Number(hours),
        dateFrom: moment(dateFrom).format('YYYY-MM-DD'),
        dateTo: moment(dateTo).format('YYYY-MM-DD'),
        ...(serviceTypeProvider?.value ? { serviceTypeProvider: serviceTypeProvider.value } : {}),
    });

    const handleSubmit = () => {
        if (!isValid) return;
        setHasSearched(true);
        getData(buildParams());
    };

    const handleCsvExport = useCallback(() => {
        if (!isValid) return;
        getDataCsv(buildParams());
    }, [isValid, comparison, period, serviceTypeProvider, hours, dateFrom, dateTo, getDataCsv]);

    const handleGridData = useCallback(
        (params: any) => {
            if (!isValid) return;
            getData({ ...buildParams(), ...params });
        },
        [isValid, comparison, period, serviceTypeProvider, hours, dateFrom, dateTo, getData],
    );

    return (
        <RoleLayout>
            <div className={classNames('pageHeading')}>
                <h2 className={classNames('pageHeadingTitle')}>INVOICED HOURS REPORT</h2>
                {isValid && (
                    <button type="button" className="btn btn-outline-info" onClick={handleCsvExport}>
                        <FontAwesomeIcon className="mr-1" icon={faDownload} />
                        Export .csv
                    </button>
                )}
            </div>

            <div className={classNames(gridStyles.firstFilterBlock, styles.filterWrap)}>
                <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 180 }}>
                    <span>Invoiced hours should be: </span>
                    <DropdownList
                        data={comparisonOptions}
                        textField="label"
                        valueField="value"
                        value={comparison}
                        onChange={(item: SelectObject) => setComparison(item)}
                        placeholder=""
                    />
                </div>

                <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span>Amount of invoiced hours: </span>
                    <input
                        type="number"
                        min={0}
                        className={classNames('form-control', 'input-sm')}
                        value={hours}
                        onChange={(e) => setHours(e.target.value)}
                        placeholder=""
                    />
                </div>

                <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 180 }}>
                    <span>Per a day or a week: </span>
                    <DropdownList
                        data={periodOptions}
                        textField="label"
                        valueField="value"
                        value={period}
                        onChange={(item: SelectObject) => setPeriod(item)}
                        placeholder=""
                    />
                </div>

                <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 180 }}>
                    <span>Service type provider: </span>
                    <DropdownList
                        data={serviceTypeProviderOptions}
                        textField="label"
                        valueField="value"
                        value={serviceTypeProvider}
                        onChange={(item: SelectObject) => setServiceTypeProvider(item)}
                        placeholder=""
                    />
                </div>

                <div className={classNames('w-100', styles.secondRow)}>
                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')}>
                        <span>Form: </span>
                        <DateTimePicker
                            value={dateFrom}
                            onChange={(date: Date | undefined) => setDateFrom(date ?? undefined)}
                            time={false}
                            date={true}
                            format={'MMM D, YYYY'}
                        />
                    </div>

                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3 mr-3')}>
                        <span>To: </span>
                        <DateTimePicker
                            value={dateTo}
                            onChange={(date: Date | undefined) => setDateTo(date ?? undefined)}
                            time={false}
                            date={true}
                            format={'MMM D, YYYY'}
                        />
                    </div>

                    <div className={classNames(gridStyles.itemFilterBlock, 'mb-3')}>
                        <button type="button" className="btn btn-info" disabled={!isValid} onClick={handleSubmit}>
                            Generate Report
                        </button>
                    </div>
                </div>
            </div>

            <div className={classNames(styles.list)}>
                {inProgress ? <Loader /> : null}
                {hasSearched || inProgress ? <WorkedHoursReportGrid data={data} getData={handleGridData} /> : null}
            </div>
        </RoleLayout>
    );
};

export default WorkedHoursReportList;
